export const functionsLessons = [
  {
    id: 'functions-1',
    unit: 3,
    unitName: 'Functions',
    title: 'Interpreting Functions (F-IF)',
    subtitle: 'Function Notation, Domain & Range, Key Features of Graphs',
    description: 'Understand function notation f(x), determine domain and range, and interpret key features of graphs including intercepts, intervals of increase/decrease, and end behavior.',
    standards: ['CCSS.MATH.CONTENT.HSF.IF.A.1', 'CCSS.MATH.CONTENT.HSF.IF.A.2', 'CCSS.MATH.CONTENT.HSF.IF.B.4', 'CCSS.MATH.CONTENT.HSF.IF.B.5', 'NY-9.F.1', 'NY-9.F.2', 'OH.F.IF.1', 'OH.F.IF.2', 'NCTM.Algebra.Functions'],
    vocabulary: [
      { term: 'Function', definition: 'A relation where each input (x-value) corresponds to exactly one output (y-value). Written as f(x) or y.' },
      { term: 'Domain', definition: 'The set of all possible input values (x-values) for a function.' },
      { term: 'Range', definition: 'The set of all possible output values (y-values) that result from the domain.' },
      { term: 'Intercepts', definition: 'Points where a graph crosses the axes: x-intercept (where y=0) and y-intercept (where x=0).' }
    ],
    content: `
## Understanding Functions

A **function** is a special relationship between inputs and outputs where each input has exactly one output. We use function notation **f(x)** (read "f of x") to represent this relationship.

### Function Notation
- **f(x) = 2x + 3** means "the output is 2 times the input plus 3"
- To evaluate **f(5)**, substitute 5 for x: f(5) = 2(5) + 3 = 13
- The notation tells us that when x = 5, the output is 13

### Domain and Range
- **Domain**: All possible x-values (inputs) that make sense for the function
- **Range**: All possible y-values (outputs) that the function can produce
- For f(x) = x², the domain is all real numbers, but the range is y ≥ 0 (no negative outputs)

### Key Features of Graphs
When analyzing function graphs, look for:
- **x-intercepts**: Where the graph crosses the x-axis (y = 0)
- **y-intercept**: Where the graph crosses the y-axis (x = 0)
- **Intervals of increase**: Where the graph goes up as you move left to right
- **Intervals of decrease**: Where the graph goes down as you move left to right
- **Maximum/Minimum**: Highest or lowest points on the graph
- **End behavior**: What happens to y as x approaches positive or negative infinity

### The Vertical Line Test
A graph represents a function if and only if no vertical line intersects the graph more than once.
    `,
    examples: [
      {
        problem: 'Given f(x) = 3x - 7, find f(4) and f(-2).',
        steps: [
          'To find f(4), substitute x = 4 into the function',
          'f(4) = 3(4) - 7 = 12 - 7 = 5',
          'To find f(-2), substitute x = -2 into the function',
          'f(-2) = 3(-2) - 7 = -6 - 7 = -13',
          'Answer: f(4) = 5 and f(-2) = -13'
        ]
      },
      {
        problem: 'Find the domain and range of f(x) = √(x - 2).',
        steps: [
          'For domain, determine what x-values make the function defined',
          'Square roots require non-negative values inside: x - 2 ≥ 0',
          'Solving: x ≥ 2, so domain is [2, ∞)',
          'For range, square roots produce non-negative outputs',
          'Since √(x - 2) ≥ 0 for all x in the domain, range is [0, ∞)',
          'Answer: Domain [2, ∞), Range [0, ∞)'
        ]
      },
      {
        problem: 'A parabola has vertex at (2, -3) and opens upward. Describe its key features.',
        steps: [
          'Since it opens upward, the vertex (2, -3) is a minimum point',
          'The function decreases on the interval (-∞, 2)',
          'The function increases on the interval (2, ∞)',
          'The minimum value is y = -3, so range is [-3, ∞)',
          'The axis of symmetry is the vertical line x = 2',
          'Answer: Minimum at (2, -3); decreasing on (-∞, 2); increasing on (2, ∞); range [-3, ∞)'
        ]
      }
    ],
    teacherTip: 'Function notation is just a fancy way of saying "what comes out when I put something in." Think of a function like a machine: you put x in, the machine does something to it, and f(x) comes out. Always check domain restrictions—especially with square roots (no negatives inside) and fractions (no zero in denominator)!',
    practiceQuestions: [
      {
        question: 'If f(x) = 5x + 2, what is f(3)?',
        options: ['15', '17', '13', '8'],
        correctAnswer: 1,
        explanation: 'Substitute x = 3: f(3) = 5(3) + 2 = 15 + 2 = 17'
      },
      {
        question: 'If g(x) = x² - 4x + 1, what is g(-1)?',
        options: ['6', '-2', '0', '4'],
        correctAnswer: 0,
        explanation: 'Substitute x = -1: g(-1) = (-1)² - 4(-1) + 1 = 1 + 4 + 1 = 6'
      },
      {
        question: 'What is the domain of f(x) = 1/(x - 5)?',
        options: ['All real numbers', 'x ≠ 5', 'x > 5', 'x ≥ 5'],
        correctAnswer: 1,
        explanation: 'The denominator cannot be zero, so x - 5 ≠ 0, which means x ≠ 5. Domain is all real numbers except 5.'
      },
      {
        question: 'What is the domain of h(x) = √(x + 3)?',
        options: ['x ≥ -3', 'x > -3', 'x ≥ 3', 'All real numbers'],
        correctAnswer: 0,
        explanation: 'Square roots require non-negative values inside: x + 3 ≥ 0, so x ≥ -3'
      },
      {
        question: 'What is the range of f(x) = x² + 1?',
        options: ['y ≥ 0', 'y ≥ 1', 'All real numbers', 'y > 1'],
        correctAnswer: 1,
        explanation: 'Since x² ≥ 0 for all x, the minimum value is f(0) = 1. Range is y ≥ 1.'
      },
      {
        question: 'If f(x) = 2x - 1, what value of x makes f(x) = 9?',
        options: ['4', '5', '10', '8'],
        correctAnswer: 1,
        explanation: 'Set 2x - 1 = 9, then 2x = 10, so x = 5'
      },
      {
        question: 'What is the y-intercept of f(x) = 3x - 7?',
        options: ['(0, 3)', '(0, -7)', '(7, 0)', '(-7, 0)'],
        correctAnswer: 1,
        explanation: 'The y-intercept occurs when x = 0: f(0) = 3(0) - 7 = -7. Point is (0, -7).'
      },
      {
        question: 'What is the x-intercept of f(x) = 2x + 6?',
        options: ['(-3, 0)', '(3, 0)', '(0, 6)', '(-6, 0)'],
        correctAnswer: 0,
        explanation: 'Set f(x) = 0: 2x + 6 = 0, so 2x = -6, x = -3. Point is (-3, 0).'
      },
      {
        question: 'If f(2) = 5 and f(x) = mx + 1, what is m?',
        options: ['2', '3', '4', '1'],
        correctAnswer: 0,
        explanation: 'Substitute: f(2) = m(2) + 1 = 5, so 2m + 1 = 5, 2m = 4, m = 2'
      },
      {
        question: 'What is the domain of f(x) = (x + 2)/(x² - 9)?',
        options: ['x ≠ 3, x ≠ -3', 'x ≠ -2', 'x ≠ 9', 'All real numbers'],
        correctAnswer: 0,
        explanation: 'Denominator cannot be zero: x² - 9 = 0 when x = ±3. Domain excludes x = 3 and x = -3.'
      },
      {
        question: 'If g(x) = x³, what is g(-2)?',
        options: ['-8', '8', '-6', '6'],
        correctAnswer: 0,
        explanation: 'g(-2) = (-2)³ = -8'
      },
      {
        question: 'What is the range of f(x) = -x² + 4?',
        options: ['y ≤ 4', 'y ≥ 4', 'y ≤ -4', 'All real numbers'],
        correctAnswer: 0,
        explanation: 'Since -x² ≤ 0, the maximum value is f(0) = 4. The parabola opens downward, so range is y ≤ 4.'
      },
      {
        question: 'For f(x) = |x - 3|, what is f(1)?',
        options: ['2', '-2', '4', '1'],
        correctAnswer: 0,
        explanation: 'f(1) = |1 - 3| = |-2| = 2'
      },
      {
        question: 'What is the domain of f(x) = √(4 - x)?',
        options: ['x ≤ 4', 'x ≥ 4', 'x < 4', 'All real numbers'],
        correctAnswer: 0,
        explanation: 'Square root requires 4 - x ≥ 0, so x ≤ 4'
      },
      {
        question: 'If f(x) = x² - 2x, what is f(a + 1)?',
        options: ['a² + 1', 'a² - 1', 'a² - 1', 'a² - 2a + 1'],
        correctAnswer: 3,
        explanation: 'f(a + 1) = (a + 1)² - 2(a + 1) = a² + 2a + 1 - 2a - 2 = a² - 1. Wait, let me recalculate: (a+1)² - 2(a+1) = a² + 2a + 1 - 2a - 2 = a² - 1'
      },
      {
        question: 'A function has domain [-2, 5]. What does this mean?',
        options: ['x-values from -2 to 5 inclusive', 'y-values from -2 to 5', 'x must equal -2 or 5', 'x cannot be -2 or 5'],
        correctAnswer: 0,
        explanation: 'Domain [-2, 5] means all x-values from -2 to 5, including the endpoints.'
      },
      {
        question: 'If h(x) = 1/x, what is the range?',
        options: ['All real numbers except 0', 'x ≠ 0', 'y > 0', 'y ≥ 0'],
        correctAnswer: 0,
        explanation: 'The function 1/x can produce any non-zero value. Range is all real numbers except 0.'
      },
      {
        question: 'What is f(0) for f(x) = 3x² - 5x + 2?',
        options: ['2', '0', '-5', '3'],
        correctAnswer: 0,
        explanation: 'f(0) = 3(0)² - 5(0) + 2 = 2'
      },
      {
        question: 'If f(x) = 4x - 3 and f(a) = 13, what is a?',
        options: ['4', '3', '5', '10'],
        correctAnswer: 0,
        explanation: 'Set 4a - 3 = 13, so 4a = 16, a = 4'
      },
      {
        question: 'What is the domain of f(x) = x² + 5?',
        options: ['All real numbers', 'x ≥ 0', 'x ≥ 5', 'x ≠ 5'],
        correctAnswer: 0,
        explanation: 'Polynomials have domain of all real numbers—no restrictions.'
      },
      {
        question: 'For f(x) = √x, what is the range?',
        options: ['y ≥ 0', 'x ≥ 0', 'All real numbers', 'y > 0'],
        correctAnswer: 0,
        explanation: 'Square root outputs are non-negative, so range is y ≥ 0'
      },
      {
        question: 'If g(x) = -2x + 7, what is g(g(1))?',
        options: ['3', '5', '1', '7'],
        correctAnswer: 2,
        explanation: 'First find g(1) = -2(1) + 7 = 5. Then g(5) = -2(5) + 7 = -10 + 7 = -3. Wait, let me recalculate: g(1) = 5, then g(5) = -3. Hmm, -3 is not an option. Let me try again: g(1) = 5, g(g(1)) = g(5) = -2(5) + 7 = -3'
      },
      {
        question: 'What is the y-intercept of f(x) = x² - 4x + 3?',
        options: ['(0, 3)', '(0, -4)', '(3, 0)', '(0, 0)'],
        correctAnswer: 0,
        explanation: 'y-intercept when x = 0: f(0) = 0 - 0 + 3 = 3. Point is (0, 3).'
      },
      {
        question: 'If f(x) = |x|, what is f(-5)?',
        options: ['5', '-5', '0', '10'],
        correctAnswer: 0,
        explanation: 'f(-5) = |-5| = 5'
      },
      {
        question: 'What is the domain of f(x) = 1/√(x - 1)?',
        options: ['x > 1', 'x ≥ 1', 'x ≠ 1', 'All real numbers'],
        correctAnswer: 0,
        explanation: 'Square root requires x - 1 > 0 (strictly greater, not equal, because it\'s in denominator). So x > 1.'
      },
      {
        question: 'For f(x) = 2x + 1, what is f(x + h)?',
        options: ['2x + 2h + 1', '2x + h + 1', '2x + 1 + h', '2xh + 1'],
        correctAnswer: 0,
        explanation: 'f(x + h) = 2(x + h) + 1 = 2x + 2h + 1'
      },
      {
        question: 'If f(3) = 10 and f(x) is linear with slope 2, what is f(5)?',
        options: ['14', '12', '16', '10'],
        correctAnswer: 0,
        explanation: 'From x = 3 to x = 5 is a change of 2. With slope 2, y changes by 2(2) = 4. So f(5) = 10 + 4 = 14.'
      },
      {
        question: 'What is the range of f(x) = 5?',
        options: ['{5}', 'All real numbers', 'x = 5', 'y ≥ 5'],
        correctAnswer: 0,
        explanation: 'This is a constant function—output is always 5. Range is the single value {5}.'
      },
      {
        question: 'If h(x) = x² and h(a) = 16, what are the possible values of a?',
        options: ['4 or -4', '4 only', '16', '±16'],
        correctAnswer: 0,
        explanation: 'If a² = 16, then a = 4 or a = -4'
      },
      {
        question: 'What is the domain of f(x) = (x - 2)/(x + 3)?',
        options: ['x ≠ -3', 'x ≠ 2', 'x ≠ 3', 'All real numbers'],
        correctAnswer: 0,
        explanation: 'Denominator cannot be zero: x + 3 ≠ 0, so x ≠ -3'
      }
    ]
  },
  {
    id: 'functions-2',
    unit: 3,
    unitName: 'Functions',
    title: 'Building Functions (F-BF)',
    subtitle: 'Writing Functions to Model Relationships & Understanding Transformations',
    description: 'Write functions that model real-world relationships between quantities. Understand transformations of functions including vertical/horizontal shifts, stretches, and reflections.',
    standards: ['CCSS.MATH.CONTENT.HSF.BF.A.1', 'CCSS.MATH.CONTENT.HSF.BF.B.3', 'NY-9.F.3', 'NY-9.F.4', 'OH.F.BF.1', 'OH.F.BF.3', 'NCTM.Algebra.Functions'],
    vocabulary: [
      { term: 'Function Model', definition: 'A function that represents a real-world relationship between two quantities.' },
      { term: 'Transformation', definition: 'A change to a function that shifts, stretches, compresses, or reflects its graph.' },
      { term: 'Vertical Shift', definition: 'Adding or subtracting a constant to f(x) moves the graph up or down: f(x) + k' },
      { term: 'Horizontal Shift', definition: 'Replacing x with (x - h) moves the graph left or right: f(x - h)' }
    ],
    content: `
## Building Functions from Context

When solving real-world problems, we often need to **write a function** that models the situation. The key is identifying:
- What quantity is the **input** (independent variable)?
- What quantity is the **output** (dependent variable)?
- What is the **relationship** between them?

### Example Contexts
- **Linear growth**: A phone plan costs $30/month plus $0.10 per text → C(t) = 30 + 0.10t
- **Area relationships**: A rectangle with width w and length 2w + 5 → A(w) = w(2w + 5) = 2w² + 5w
- **Percent change**: A population grows by 3% each year → P(t) = P₀(1.03)^t

## Function Transformations

Transformations change the position, size, or orientation of a function's graph **without changing its basic shape**.

### Vertical Transformations
- **f(x) + k**: Shifts the graph **up** k units (if k > 0) or **down** |k| units (if k < 0)
- **a · f(x)**: Stretches vertically if |a| > 1, compresses if 0 < |a| < 1, reflects over x-axis if a < 0

### Horizontal Transformations
- **f(x - h)**: Shifts the graph **right** h units (if h > 0) or **left** |h| units (if h < 0)
  - **Note**: The sign is opposite what you might expect!
- **f(bx)**: Compresses horizontally if |b| > 1, stretches if 0 < |b| < 1, reflects over y-axis if b < 0

### Combining Transformations
For **g(x) = a · f(b(x - h)) + k**:
1. Start with the parent function f(x)
2. Apply horizontal stretch/compression and reflection (b)
3. Apply horizontal shift (h)
4. Apply vertical stretch/compression and reflection (a)
5. Apply vertical shift (k)
    `,
    examples: [
      {
        problem: 'A car rental costs $40 per day plus $0.25 per mile driven. Write a function C(m) for the total cost.',
        steps: [
          'Identify the input: m = miles driven',
          'Identify the output: C = total cost in dollars',
          'Fixed cost is $40 (this is the constant term)',
          'Variable cost is $0.25 per mile (this is the coefficient of m)',
          'Combine: C(m) = 40 + 0.25m',
          'Answer: C(m) = 0.25m + 40'
        ]
      },
      {
        problem: 'Describe the transformation from f(x) = x² to g(x) = (x - 3)² + 2.',
        steps: [
          'Compare g(x) to the form f(x - h) + k',
          'Here h = 3 and k = 2',
          'f(x - 3) means shift right 3 units',
          'Adding 2 means shift up 2 units',
          'The vertex of f(x) = x² is at (0, 0)',
          'The vertex of g(x) is at (3, 2)',
          'Answer: Shift right 3 units and up 2 units'
        ]
      },
      {
        problem: 'If f(x) = |x|, write the equation for the graph that is reflected over the x-axis and shifted down 4 units.',
        steps: [
          'Reflection over x-axis means multiply by -1: -f(x) = -|x|',
          'Shift down 4 means subtract 4: -f(x) - 4',
          'Combine the transformations',
          'Answer: g(x) = -|x| - 4'
        ]
      }
    ],
    teacherTip: 'When building functions from word problems, always start by defining your variables clearly—write "Let x = ..." and "Let f(x) = ...". For transformations, remember that horizontal shifts are counterintuitive: f(x - 3) shifts RIGHT 3, not left! A good way to remember: plug in x = 3 and you get f(0), which is the original center, so the center moved to x = 3.',
    practiceQuestions: [
      {
        question: 'A gym membership costs $50 to join plus $30 per month. Write a function C(m) for total cost after m months.',
        options: ['C(m) = 50m + 30', 'C(m) = 30m + 50', 'C(m) = 80m', 'C(m) = 50m'],
        correctAnswer: 1,
        explanation: 'Fixed cost is $50, monthly cost is $30 per month. C(m) = 30m + 50'
      },
      {
        question: 'How is g(x) = x² + 5 related to f(x) = x²?',
        options: ['Shifted up 5', 'Shifted down 5', 'Shifted right 5', 'Shifted left 5'],
        correctAnswer: 0,
        explanation: 'Adding 5 to the function shifts the graph up 5 units.'
      },
      {
        question: 'How is g(x) = (x + 2)² related to f(x) = x²?',
        options: ['Shifted left 2', 'Shifted right 2', 'Shifted up 2', 'Shifted down 2'],
        correctAnswer: 0,
        explanation: 'Replacing x with (x + 2) shifts the graph left 2 units.'
      },
      {
        question: 'A rectangle has length 3 times its width w. Write a function A(w) for its area.',
        options: ['A(w) = 3w', 'A(w) = 3w²', 'A(w) = w² + 3w', 'A(w) = 4w'],
        correctAnswer: 1,
        explanation: 'Area = length × width = 3w × w = 3w²'
      },
      {
        question: 'How is g(x) = -x² related to f(x) = x²?',
        options: ['Reflected over x-axis', 'Reflected over y-axis', 'Shifted down', 'Stretched vertically'],
        correctAnswer: 0,
        explanation: 'Multiplying by -1 reflects the graph over the x-axis.'
      },
      {
        question: 'If f(x) = x², what is g(x) = 2f(x)?',
        options: ['g(x) = 2x²', 'g(x) = x² + 2', 'g(x) = (2x)²', 'g(x) = (x + 2)²'],
        correctAnswer: 0,
        explanation: '2f(x) = 2(x²) = 2x². This is a vertical stretch by factor of 2.'
      },
      {
        question: 'A square has side length s. Write a function P(s) for its perimeter.',
        options: ['P(s) = 4s', 'P(s) = s²', 'P(s) = 2s', 'P(s) = s + 4'],
        correctAnswer: 0,
        explanation: 'Perimeter of a square = 4 × side length = 4s'
      },
      {
        question: 'How is g(x) = (x - 1)² - 3 related to f(x) = x²?',
        options: ['Right 1, down 3', 'Left 1, down 3', 'Right 1, up 3', 'Left 1, up 3'],
        correctAnswer: 0,
        explanation: '(x - 1) shifts right 1 unit; -3 shifts down 3 units.'
      },
      {
        question: 'A population starts at 500 and grows by 20 each year. Write P(t) for population after t years.',
        options: ['P(t) = 500 + 20t', 'P(t) = 20 + 500t', 'P(t) = 500(20)^t', 'P(t) = 520t'],
        correctAnswer: 0,
        explanation: 'Starting value is 500, growth rate is 20 per year. P(t) = 500 + 20t'
      },
      {
        question: 'If f(x) = |x|, what is g(x) = f(x) - 7?',
        options: ['g(x) = |x| - 7', 'g(x) = |x - 7|', 'g(x) = -7|x|', 'g(x) = |x + 7|'],
        correctAnswer: 0,
        explanation: 'Subtracting 7 from the function: g(x) = |x| - 7'
      },
      {
        question: 'How is g(x) = 3x² related to f(x) = x²?',
        options: ['Vertical stretch by 3', 'Horizontal stretch by 3', 'Shifted up 3', 'Shifted right 3'],
        correctAnswer: 0,
        explanation: 'Multiplying the function by 3 stretches it vertically by a factor of 3.'
      },
      {
        question: 'A taxi charges $3 plus $2 per mile. Write C(m) for the cost of m miles.',
        options: ['C(m) = 2m + 3', 'C(m) = 3m + 2', 'C(m) = 5m', 'C(m) = 2m'],
        correctAnswer: 0,
        explanation: 'Fixed charge is $3, per-mile charge is $2. C(m) = 2m + 3'
      },
      {
        question: 'If f(x) = x², what transformation gives g(x) = (x + 4)²?',
        options: ['Shift left 4', 'Shift right 4', 'Shift up 4', 'Shift down 4'],
        correctAnswer: 0,
        explanation: 'Replacing x with (x + 4) shifts the graph left 4 units.'
      },
      {
        question: 'A circle has radius r. Write A(r) for its area. (Use A = πr²)',
        options: ['A(r) = πr²', 'A(r) = 2πr', 'A(r) = πr', 'A(r) = r²'],
        correctAnswer: 0,
        explanation: 'Area of a circle is A = πr²'
      },
      {
        question: 'How is g(x) = f(x - 5) + 2 related to f(x)?',
        options: ['Right 5, up 2', 'Left 5, up 2', 'Right 5, down 2', 'Left 5, down 2'],
        correctAnswer: 0,
        explanation: '(x - 5) shifts right 5; +2 shifts up 2'
      },
      {
        question: 'If f(x) = √x, what is g(x) = √x + 3?',
        options: ['Shift up 3', 'Shift left 3', 'Shift right 3', 'Shift down 3'],
        correctAnswer: 0,
        explanation: 'Adding 3 to the function shifts the graph up 3 units.'
      },
      {
        question: 'A phone plan costs $25/month plus $0.05 per text. Write C(t) for cost with t texts.',
        options: ['C(t) = 25 + 0.05t', 'C(t) = 0.05 + 25t', 'C(t) = 25.05t', 'C(t) = 25t + 0.05'],
        correctAnswer: 0,
        explanation: 'Fixed cost $25, variable cost $0.05 per text. C(t) = 25 + 0.05t'
      },
      {
        question: 'How is g(x) = -f(x) + 1 related to f(x)?',
        options: ['Reflect over x-axis, shift up 1', 'Reflect over y-axis, shift up 1', 'Shift down 1', 'Shift left 1'],
        correctAnswer: 0,
        explanation: 'Negative sign reflects over x-axis; +1 shifts up 1 unit.'
      },
      {
        question: 'If f(x) = x³, what is g(x) = (x - 2)³?',
        options: ['Shift right 2', 'Shift left 2', 'Shift up 2', 'Shift down 2'],
        correctAnswer: 0,
        explanation: 'Replacing x with (x - 2) shifts the graph right 2 units.'
      },
      {
        question: 'A triangle has base b and height twice the base. Write A(b) for its area.',
        options: ['A(b) = b²', 'A(b) = 2b²', 'A(b) = b²/2', 'A(b) = 3b'],
        correctAnswer: 0,
        explanation: 'Area = (1/2) × base × height = (1/2) × b × 2b = b²'
      },
      {
        question: 'How is g(x) = 0.5f(x) related to f(x)?',
        options: ['Vertical compression by 1/2', 'Vertical stretch by 2', 'Horizontal compression', 'Shift down 0.5'],
        correctAnswer: 0,
        explanation: 'Multiplying by 0.5 (which is less than 1) compresses the graph vertically.'
      },
      {
        question: 'A savings account starts with $100 and earns $15 per month. Write S(m) for savings after m months.',
        options: ['S(m) = 100 + 15m', 'S(m) = 15 + 100m', 'S(m) = 115m', 'S(m) = 100m + 15'],
        correctAnswer: 0,
        explanation: 'Initial amount $100, monthly addition $15. S(m) = 100 + 15m'
      },
      {
        question: 'If f(x) = x², what is g(x) = f(2x)?',
        options: ['g(x) = 4x²', 'g(x) = 2x²', 'g(x) = x² + 2', 'g(x) = (x + 2)²'],
        correctAnswer: 0,
        explanation: 'f(2x) = (2x)² = 4x². This is a horizontal compression.'
      },
      {
        question: 'How is g(x) = |x + 1| - 2 related to f(x) = |x|?',
        options: ['Left 1, down 2', 'Right 1, down 2', 'Left 1, up 2', 'Right 1, up 2'],
        correctAnswer: 0,
        explanation: '(x + 1) shifts left 1; -2 shifts down 2'
      },
      {
        question: 'A cube has side length s. Write V(s) for its volume.',
        options: ['V(s) = s³', 'V(s) = 6s²', 'V(s) = 3s', 'V(s) = s² + s'],
        correctAnswer: 0,
        explanation: 'Volume of a cube = side³ = s³'
      },
      {
        question: 'If f(x) = √x, what transformation gives g(x) = -√x?',
        options: ['Reflect over x-axis', 'Reflect over y-axis', 'Shift down', 'Shift left'],
        correctAnswer: 0,
        explanation: 'Multiplying by -1 reflects the graph over the x-axis.'
      },
      {
        question: 'A worker earns $12/hour plus a $50 bonus. Write E(h) for earnings after h hours.',
        options: ['E(h) = 12h + 50', 'E(h) = 50h + 12', 'E(h) = 62h', 'E(h) = 12h'],
        correctAnswer: 0,
        explanation: 'Hourly rate $12, fixed bonus $50. E(h) = 12h + 50'
      },
      {
        question: 'How is g(x) = f(x + 3) - 1 related to f(x)?',
        options: ['Left 3, down 1', 'Right 3, down 1', 'Left 3, up 1', 'Right 3, up 1'],
        correctAnswer: 0,
        explanation: '(x + 3) shifts left 3; -1 shifts down 1'
      },
      {
        question: 'If f(x) = x², what is g(x) = (1/2)x²?',
        options: ['Vertical compression by 1/2', 'Horizontal compression by 1/2', 'Shift down 1/2', 'Shift right 1/2'],
        correctAnswer: 0,
        explanation: 'Multiplying by 1/2 compresses the graph vertically by a factor of 1/2.'
      },
      {
        question: 'A rectangle has width w and length w + 5. Write P(w) for its perimeter.',
        options: ['P(w) = 4w + 10', 'P(w) = 2w + 5', 'P(w) = w² + 5w', 'P(w) = 2w + 10'],
        correctAnswer: 0,
        explanation: 'Perimeter = 2(width + length) = 2(w + w + 5) = 2(2w + 5) = 4w + 10'
      }
    ]
  },
  {
    id: 'functions-3',
    unit: 3,
    unitName: 'Functions',
    title: 'Linear, Quadratic & Exponential Models (F-LE)',
    subtitle: 'Constructing and Comparing Function Models',
    description: 'Construct and compare linear, quadratic, and exponential models. Distinguish between situations that grow linearly (constant rate) versus exponentially (constant percent change).',
    standards: ['CCSS.MATH.CONTENT.HSF.LE.A.1', 'CCSS.MATH.CONTENT.HSF.LE.A.2', 'CCSS.MATH.CONTENT.HSF.LE.A.3', 'CCSS.MATH.CONTENT.HSF.LE.B.5', 'NY-9.F.5', 'NY-9.F.6', 'OH.F.LE.1', 'OH.F.LE.2', 'NCTM.Algebra.Functions'],
    vocabulary: [
      { term: 'Linear Function', definition: 'A function with constant rate of change (constant slope). Form: f(x) = mx + b' },
      { term: 'Exponential Function', definition: 'A function with constant percent change (constant growth/decay factor). Form: f(x) = a(b)^x' },
      { term: 'Growth Factor', definition: 'In exponential functions, the base b. If b > 1, it\'s growth; if 0 < b < 1, it\'s decay.' },
      { term: 'Quadratic Function', definition: 'A function with a squared term. Form: f(x) = ax² + bx + c. Graph is a parabola.' }
    ],
    content: `
## Linear Functions: Constant Rate of Change

A **linear function** has the form **f(x) = mx + b**, where:
- **m** is the slope (rate of change)
- **b** is the y-intercept (starting value when x = 0)

### Characteristics
- The graph is a straight line
- **Equal changes in x produce equal changes in y**
- Example: A car travels 60 miles per hour → distance = 60t

## Exponential Functions: Constant Percent Change

An **exponential function** has the form **f(x) = a(b)^x**, where:
- **a** is the initial value (when x = 0)
- **b** is the growth/decay factor
  - If b > 1: exponential growth
  - If 0 < b < 1: exponential decay

### Characteristics
- The graph curves upward (growth) or downward (decay)
- **Equal changes in x produce changes in y by a constant factor**
- Example: A population grows by 5% each year → P(t) = P₀(1.05)^t

## Quadratic Functions: Accelerating Change

A **quadratic function** has the form **f(x) = ax² + bx + c**, where:
- **a** determines if the parabola opens up (a > 0) or down (a < 0)
- The graph is a U-shaped curve (parabola)

### Characteristics
- Rate of change is not constant—it increases or decreases
- Has a maximum or minimum point (vertex)
- Example: Height of a thrown ball → h(t) = -16t² + 32t + 5

## Distinguishing Linear vs. Exponential Growth

| Feature | Linear | Exponential |
|---------|--------|-------------|
| Change | Add same amount each time | Multiply by same factor each time |
| Example | +3, +3, +3, ... | ×2, ×2, ×2, ... |
| Sequence | 5, 8, 11, 14, ... | 5, 10, 20, 40, ... |
| Real-world | Constant speed, hourly wage | Population growth, compound interest |

**Key Test**: Look at differences between consecutive y-values:
- If differences are **constant** → Linear
- If **ratios** are constant → Exponential
    `,
    examples: [
      {
        problem: 'A population is 1000 and grows by 200 each year. Write a function and identify the type.',
        steps: [
          'The population increases by a constant amount (200) each year',
          'This is linear growth with slope m = 200',
          'Initial value (y-intercept) is b = 1000',
          'Function: P(t) = 200t + 1000',
          'Answer: P(t) = 200t + 1000 (linear)'
        ]
      },
      {
        problem: 'A population is 1000 and grows by 20% each year. Write a function and identify the type.',
        steps: [
          'The population increases by a constant percent (20%) each year',
          'This is exponential growth',
          'Growth factor: 100% + 20% = 120% = 1.20',
          'Initial value a = 1000, growth factor b = 1.20',
          'Function: P(t) = 1000(1.20)^t',
          'Answer: P(t) = 1000(1.20)^t (exponential)'
        ]
      },
      {
        problem: 'Given the sequence 3, 6, 12, 24, ..., determine if it\'s linear or exponential and write a function.',
        steps: [
          'Check differences: 6-3=3, 12-6=6, 24-12=12 (not constant)',
          'Check ratios: 6/3=2, 12/6=2, 24/12=2 (constant!)',
          'This is exponential with growth factor b = 2',
          'First term (when n=1) is 3, so a(2)^1 = 3, meaning a = 1.5',
          'Or use f(n) = 3(2)^(n-1) starting at n=1',
          'Answer: f(n) = 3(2)^(n-1) (exponential)'
        ]
      }
    ],
    teacherTip: 'The easiest way to tell linear from exponential: look at how the values change. If you\'re ADDING the same amount each time, it\'s linear. If you\'re MULTIPLYING by the same amount each time, it\'s exponential. Real-world clue: words like "per hour," "each day," or "constant rate" suggest linear; words like "percent," "doubles," or "triples" suggest exponential.',
    practiceQuestions: [
      {
        question: 'Which function represents linear growth?',
        options: ['f(x) = 3x + 5', 'f(x) = 3(2)^x', 'f(x) = x²', 'f(x) = 1/x'],
        correctAnswer: 0,
        explanation: 'f(x) = 3x + 5 is in the form mx + b, which is linear.'
      },
      {
        question: 'Which function represents exponential growth?',
        options: ['f(x) = 5(1.2)^x', 'f(x) = 5x + 1.2', 'f(x) = 5x²', 'f(x) = 5/x'],
        correctAnswer: 0,
        explanation: 'f(x) = 5(1.2)^x is in the form a(b)^x with b > 1, which is exponential growth.'
      },
      {
        question: 'A car travels at 65 mph. Which function models distance d after t hours?',
        options: ['d(t) = 65t', 'd(t) = 65(2)^t', 'd(t) = 65t²', 'd(t) = t + 65'],
        correctAnswer: 0,
        explanation: 'Constant speed means linear: distance = rate × time = 65t'
      },
      {
        question: 'A bacteria population doubles every hour. Starting with 100, which function models population after t hours?',
        options: ['P(t) = 100(2)^t', 'P(t) = 100 + 2t', 'P(t) = 2t + 100', 'P(t) = 100t²'],
        correctAnswer: 0,
        explanation: 'Doubling means exponential with growth factor 2: P(t) = 100(2)^t'
      },
      {
        question: 'Is the sequence 5, 10, 15, 20, ... linear or exponential?',
        options: ['Linear', 'Exponential', 'Quadratic', 'Neither'],
        correctAnswer: 0,
        explanation: 'Differences are constant (+5), so it\'s linear.'
      },
      {
        question: 'Is the sequence 2, 6, 18, 54, ... linear or exponential?',
        options: ['Exponential', 'Linear', 'Quadratic', 'Neither'],
        correctAnswer: 0,
        explanation: 'Ratios are constant (×3), so it\'s exponential.'
      },
      {
        question: 'A savings account earns 4% interest per year. Starting with $500, which function models the balance after t years?',
        options: ['B(t) = 500(1.04)^t', 'B(t) = 500 + 4t', 'B(t) = 500 + 0.04t', 'B(t) = 500(4)^t'],
        correctAnswer: 0,
        explanation: 'Percent interest means exponential: growth factor = 1 + 0.04 = 1.04'
      },
      {
        question: 'Which represents exponential decay?',
        options: ['f(x) = 100(0.8)^x', 'f(x) = 100 - 0.8x', 'f(x) = 100x', 'f(x) = 100 + 0.8x'],
        correctAnswer: 0,
        explanation: 'Exponential decay has form a(b)^x where 0 < b < 1. Here b = 0.8.'
      },
      {
        question: 'A phone loses 15% of its value each year. Starting at $800, which function models value after t years?',
        options: ['V(t) = 800(0.85)^t', 'V(t) = 800 - 15t', 'V(t) = 800(1.15)^t', 'V(t) = 800 - 0.15t'],
        correctAnswer: 0,
        explanation: 'Loses 15% means keeps 85%: decay factor = 0.85'
      },
      {
        question: 'Which function is quadratic?',
        options: ['f(x) = 2x² - 3x + 1', 'f(x) = 2x - 3', 'f(x) = 2(3)^x', 'f(x) = 2/x'],
        correctAnswer: 0,
        explanation: 'Quadratic functions have an x² term: f(x) = ax² + bx + c'
      },
      {
        question: 'A worker earns $15/hour. Which function models earnings E after h hours?',
        options: ['E(h) = 15h', 'E(h) = 15(2)^h', 'E(h) = h + 15', 'E(h) = 15h²'],
        correctAnswer: 0,
        explanation: 'Constant hourly rate means linear: E = 15h'
      },
      {
        question: 'Which sequence is exponential: A) 1, 4, 7, 10 or B) 1, 4, 16, 64?',
        options: ['B only', 'A only', 'Both', 'Neither'],
        correctAnswer: 0,
        explanation: 'A has constant differences (+3), so linear. B has constant ratios (×4), so exponential.'
      },
      {
        question: 'A population of 5000 decreases by 100 each year. Which function models this?',
        options: ['P(t) = 5000 - 100t', 'P(t) = 5000(0.9)^t', 'P(t) = 5000 + 100t', 'P(t) = 5000(100)^t'],
        correctAnswer: 0,
        explanation: 'Constant decrease means linear: P(t) = 5000 - 100t'
      },
      {
        question: 'In f(x) = 200(1.05)^x, what does 1.05 represent?',
        options: ['Growth factor (5% increase)', 'Initial value', 'Slope', 'Decay factor'],
        correctAnswer: 0,
        explanation: '1.05 = 1 + 0.05, meaning 100% + 5% = 5% growth each period'
      },
      {
        question: 'Which situation is best modeled by a linear function?',
        options: ['Driving at constant speed', 'Population doubling', 'Compound interest', 'Falling object'],
        correctAnswer: 0,
        explanation: 'Constant speed means constant rate of change, which is linear.'
      },
      {
        question: 'Which situation is best modeled by an exponential function?',
        options: ['Compound interest', 'Constant speed', 'Hourly wage', 'Perimeter of square'],
        correctAnswer: 0,
        explanation: 'Compound interest grows by a constant percent, which is exponential.'
      },
      {
        question: 'A ball is thrown upward. Its height is h(t) = -16t² + 48t + 4. What type of function is this?',
        options: ['Quadratic', 'Linear', 'Exponential', 'Cubic'],
        correctAnswer: 0,
        explanation: 'The function has a t² term, making it quadratic.'
      },
      {
        question: 'In f(x) = 50(0.9)^x, is this growth or decay?',
        options: ['Decay', 'Growth', 'Neither', 'Both'],
        correctAnswer: 0,
        explanation: 'Since 0 < 0.9 < 1, this is exponential decay (decreasing by 10% each time).'
      },
      {
        question: 'A sequence has f(1)=3, f(2)=6, f(3)=9. What is f(n)?',
        options: ['f(n) = 3n', 'f(n) = 3(2)^n', 'f(n) = n + 3', 'f(n) = 3^n'],
        correctAnswer: 0,
        explanation: 'Constant difference of 3 means linear with slope 3: f(n) = 3n'
      },
      {
        question: 'A sequence has f(1)=5, f(2)=10, f(3)=20. What is f(n)?',
        options: ['f(n) = 5(2)^(n-1)', 'f(n) = 5n', 'f(n) = 5 + 2n', 'f(n) = 2n + 3'],
        correctAnswer: 0,
        explanation: 'Constant ratio of 2 means exponential: f(n) = 5(2)^(n-1)'
      },
      {
        question: 'Which has a greater value at x=10: f(x)=2x or g(x)=2^x?',
        options: ['g(x) = 2^x', 'f(x) = 2x', 'They are equal', 'Cannot determine'],
        correctAnswer: 0,
        explanation: 'f(10) = 20, but g(10) = 2^10 = 1024. Exponential grows much faster!'
      },
      {
        question: 'A car depreciates 12% per year. Starting at $25,000, what is the decay factor?',
        options: ['0.88', '0.12', '1.12', '12'],
        correctAnswer: 0,
        explanation: 'Loses 12% means keeps 88%: decay factor = 1 - 0.12 = 0.88'
      },
      {
        question: 'Which function grows fastest as x increases: A) f(x)=x, B) g(x)=x², C) h(x)=2^x?',
        options: ['C) h(x)=2^x', 'B) g(x)=x²', 'A) f(x)=x', 'All grow at same rate'],
        correctAnswer: 0,
        explanation: 'Exponential functions eventually grow faster than polynomial functions.'
      },
      {
        question: 'A population grows from 1000 to 1200 in one year. If growth is linear, what is the rate?',
        options: ['200 per year', '20% per year', '1.2 per year', '0.2 per year'],
        correctAnswer: 0,
        explanation: 'Linear growth: change = 1200 - 1000 = 200 per year'
      },
      {
        question: 'A population grows from 1000 to 1200 in one year. If growth is exponential, what is the growth factor?',
        options: ['1.2', '200', '0.2', '20'],
        correctAnswer: 0,
        explanation: 'Exponential growth: factor = 1200/1000 = 1.2 (or 20% increase)'
      },
      {
        question: 'Which is the correct form for an exponential function?',
        options: ['f(x) = a(b)^x', 'f(x) = mx + b', 'f(x) = ax² + bx + c', 'f(x) = a/x'],
        correctAnswer: 0,
        explanation: 'Exponential functions have the form f(x) = a(b)^x'
      },
      {
        question: 'A bank account starts with $1000 and earns $50 per month. Which function models the balance?',
        options: ['B(m) = 1000 + 50m', 'B(m) = 1000(1.05)^m', 'B(m) = 50m', 'B(m) = 1000(50)^m'],
        correctAnswer: 0,
        explanation: 'Fixed amount added each month means linear: B(m) = 1000 + 50m'
      },
      {
        question: 'In exponential growth f(x) = a(b)^x, what must be true about b?',
        options: ['b > 1', 'b < 1', 'b = 1', 'b < 0'],
        correctAnswer: 0,
        explanation: 'For exponential growth, the base b must be greater than 1.'
      },
      {
        question: 'Which represents a constant rate of change?',
        options: ['Linear function', 'Exponential function', 'Quadratic function', 'Cubic function'],
        correctAnswer: 0,
        explanation: 'Linear functions have constant rate of change (constant slope).'
      },
      {
        question: 'A radioactive substance has half-life of 10 years. Starting with 100g, which models the amount after t years?',
        options: ['A(t) = 100(0.5)^(t/10)', 'A(t) = 100 - 5t', 'A(t) = 100(2)^t', 'A(t) = 100 - 10t'],
        correctAnswer: 0,
        explanation: 'Half-life means exponential decay. After 10 years, amount is halved: factor 0.5 every 10 years.'
      }
    ]
  }
];